// src/encounter/l360/l360-a3-section.tsx
// Longevity 360 A3 "Ready to activate?". The Workflows A3 gate is the ONLY decision point: this section shows the
// run's own result (status, log, revisions, holds) and sends the approver's snapshot back. It computes no verdict.
//   Approver snapshot   the run's revisions AND the Patient plan/consent revisions, stored together when the section
//                       loads; "what the approver saw". Patient null = its endpoint is not deployed (SIMULATED).
//   Approve activation  POST gates/A3/complete with the snapshot; a 409 is a rejection and is never retried.
//   Raise demo hold     a real hold on A3 in the dev run (button only when env.l360DemoHoldEnabled); the snapshot is not refreshed.
//   Release hold        an active hold gets a signed disposition note and walks to Released (same dev flag; the signature is SIMULATED).
//   After a Sign       A1 bumps `resnapshot`; the snapshot is dropped and taken again once the refetch lands.
import { useState } from 'react';
import { Button, Checkbox, cn, StatusChip, TextArea, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import {
  a3Holds,
  a3LogEntries,
  APPROVED_MESSAGE,
  blockingHolds,
  changedRevisions,
  checkedAgainst,
  CONSENT_REQUIRED_MESSAGE,
  CONSENT_UNKNOWN_MESSAGE,
  CLEARANCE_SIMULATED_REASON,
  DEMO_HOLD_NOTE,
  formatSnapshotTime,
  hasNoConsent,
  HELD_MESSAGE,
  HOLD_LABEL,
  holdIsSimulated,
  holdStepsToRelease,
  isWaitingOnA3,
  NO_CONSENT_MESSAGE,
  NO_CONSENT_REASON,
  NOT_ALLOWED_MESSAGE,
  NO_RUN_MESSAGE,
  PATIENT_REVISION_KEYS,
  PATIENT_REVISION_LABEL,
  PATIENT_SIMULATED_REASON,
  REVISION_KEYS,
  REVISION_LABEL,
  STALE_MESSAGE,
} from '../lib/l360-activation';
import {
  HoldRevisionMissingError,
  useA3Enrolment,
  useActivationSnapshot,
  useApproveA3,
  useCareModelRun,
  useRaiseDemoHold,
  useReleaseHold,
} from '../services/use-l360-activation';
import { useAuthStore } from '@/auth/auth-store';
import { ApiError } from '@/shared/api/api-client';
import { env } from '@/config/env';
import type { ApproverSnapshot, CareModelRunWire, PatientRevisions, RunHoldWire, RunRevisions } from '@/types/l360-api';

const SECONDARY = cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary);
const BODY = cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary);
const EMPHASIS = cn(TEXT_ROLE_CLASSES['body-emphasis'], TEXT_TONE_CLASSES.primary);
const SUBTITLE = cn(TEXT_ROLE_CLASSES['meta-strong'], TEXT_TONE_CLASSES.secondary);

const RELEASE_STALE_MESSAGE = 'The hold changed. Re-check and try again.';
const RELEASE_FAILED_MESSAGE = 'The hold could not be released. Nothing after the last recorded step was changed.';
const RELEASE_REVISION_MISSING_MESSAGE = 'The hold revision could not be read. Nothing after the last recorded step was changed. Re-check and try again.';
const RELEASE_NOT_RELEASABLE_MESSAGE = 'This hold cannot be released from its current state.';
const RELEASE_SIMULATED_NOTE = 'Signature not verified yet (recorded reference only)';
const RECHECK_FAILED_MESSAGE = 'Re-check failed: the run or Patient revisions could not be read. Try again.';

const takeSnapshot = (run: CareModelRunWire, patient: PatientRevisions | null): ApproverSnapshot => ({
  revisions: { ...run.revisions },
  patient: patient ? { planRevision: patient.planRevision, consentRevision: patient.consentRevision } : null,
  takenAt: new Date().toISOString(),
});

function Simulated() {
  return (
    <StatusChip semantic="warning" tone="soft">
      Simulated
    </StatusChip>
  );
}

function RevisionList({ revisions, patient }: { revisions: RunRevisions; patient: PatientRevisions | null }) {
  return (
    <ul aria-label="Run revisions" className="flex flex-col">
      {REVISION_KEYS.map((k) => (
        <li key={k} data-revision={k} className="flex flex-col gap-1 border-b border-border-subtle px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <span className={BODY}>{`${REVISION_LABEL[k]} revision`}</span>
            <span className="flex items-center gap-2">
              {k === 'clearance' && <Simulated />}
              <span className={BODY}>{String(revisions[k])}</span>
            </span>
          </div>
          {k === 'clearance' && <span className={SECONDARY}>{`Simulated: ${CLEARANCE_SIMULATED_REASON}`}</span>}
        </li>
      ))}
      {patient &&
        PATIENT_REVISION_KEYS.map((k) => (
          <li key={k} data-revision={k} className="flex items-center justify-between gap-3 border-b border-border-subtle px-3 py-2">
            <span className={BODY}>{PATIENT_REVISION_LABEL[k]}</span>
            <span className={BODY}>{String(patient[k])}</span>
          </li>
        ))}
      {!patient && (
        <li data-revision="patient-unavailable" className="flex flex-col gap-1 border-b border-border-subtle px-3 py-2">
          <span className="flex items-center gap-2">
            <Simulated />
            <span className={BODY}>Patient plan and consent revisions</span>
          </span>
          <span className={SECONDARY}>{PATIENT_SIMULATED_REASON}</span>
        </li>
      )}
    </ul>
  );
}

function ReleaseHoldForm({ enrolmentId, hold, onClose }: { enrolmentId: string; hold: RunHoldWire; onClose: () => void }) {
  const release = useReleaseHold(enrolmentId);
  const signedBy = useAuthStore((s) => s.user?.userId);
  const [note, setNote] = useState('');
  const [signed, setSigned] = useState(false);
  const needsNote = hold.acknowledgmentState !== 'disposition_recorded';
  const canSubmit = (!needsNote || note.trim() !== '') && signed && !!signedBy && !release.isPending;
  const submit = () => {
    if (!signedBy) return;
    release.mutate({ hold, ...(needsNote ? { disposition: note } : {}), signedBy }, { onSuccess: (r) => r.kind === 'released' && onClose() });
  };
  const stale = release.data?.kind === 'stale';
  return (
    <div data-release-form={hold.holdId} className="flex flex-col gap-2 py-1">
      {needsNote ? (
        <TextArea label="Disposition note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      ) : (
        <span className={SECONDARY}>{`Disposition on record: ${hold.disposition ?? 'not shown'}`}</span>
      )}
      <Checkbox label="I sign this disposition" checked={signed} onCheckedChange={(v) => setSigned(v === true)} />
      <span className="flex items-center gap-2">
        <Simulated />
        <span className={SECONDARY}>{RELEASE_SIMULATED_NOTE}</span>
      </span>
      <div className="flex items-center gap-3">
        <Button variant="primary" size="sm" onClick={submit} disabled={!canSubmit}>
          Sign and release
        </Button>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={release.isPending}>
          Cancel
        </Button>
      </div>
      {stale && <p role="alert" className={SECONDARY}>{RELEASE_STALE_MESSAGE}</p>}
      {release.data?.kind === 'not-releasable' && <p role="alert" className={SECONDARY}>{RELEASE_NOT_RELEASABLE_MESSAGE}</p>}
      {release.isError && (
        <p role="alert" className={SECONDARY}>
          {release.error instanceof HoldRevisionMissingError ? RELEASE_REVISION_MISSING_MESSAGE : RELEASE_FAILED_MESSAGE}
        </p>
      )}
    </div>
  );
}

function HoldRow({ hold, enrolmentId }: { hold: RunHoldWire; enrolmentId: string }) {
  const simulated = holdIsSimulated(hold);
  const [releasing, setReleasing] = useState(false);
  const canRelease = env.l360DemoHoldEnabled && hold.isActive && holdStepsToRelease(hold.acknowledgmentState).length > 0;
  return (
    <li data-hold={hold.holdId} className="flex flex-col gap-1 border-b border-border-subtle px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className={BODY}>{hold.reason ?? 'No reason recorded'}</span>
        <span className="flex items-center gap-2">
          {simulated && <Simulated />}
          <StatusChip semantic={hold.isActive ? 'warning' : 'neutral'} tone="soft">
            {hold.isActive ? 'Active' : 'Released'}
          </StatusChip>
        </span>
      </div>
      <span className={SECONDARY}>{`State: ${hold.acknowledgmentState ?? 'not recorded'} · Owner role: ${hold.ownerRole ?? 'not recorded'}`}</span>
      {simulated && <span className={SECONDARY}>{hold.releaseVerification}</span>}
      {canRelease && !releasing && (
        <span>
          <Button variant="secondary" size="sm" onClick={() => setReleasing(true)}>
            Release hold
          </Button>
        </span>
      )}
      {canRelease && releasing && <ReleaseHoldForm enrolmentId={enrolmentId} hold={hold} onClose={() => setReleasing(false)} />}
    </li>
  );
}

function RunBody({ enrolmentId, patientId, carePlanId, resnapshot }: { enrolmentId: string; patientId: string; carePlanId: string; resnapshot: number }) {
  const run = useCareModelRun(enrolmentId);
  const patientSnap = useActivationSnapshot(patientId, carePlanId);
  const approve = useApproveA3(enrolmentId);
  const demoHold = useRaiseDemoHold(enrolmentId);
  const [snapshot, setSnapshot] = useState<ApproverSnapshot | null>(null);
  // A successful Sign in A1 bumps `resnapshot`: drop the stored snapshot so it is taken again from the refetched data.
  const [seenResnapshot, setSeenResnapshot] = useState(resnapshot);
  if (seenResnapshot !== resnapshot) {
    setSeenResnapshot(resnapshot);
    setSnapshot(null);
  }
  const data = run.data;
  const patientData = patientSnap.data;
  // The snapshot is "what the approver saw": run and Patient revisions together, taken once when both first load
  // (`null` Patient data is an answer: the endpoint is not deployed). Setting state while rendering is the
  // React-documented way to adjust state to new data without an effect. Not while a read is in flight: after a Sign
  // the cached data is old and the refetch is under way.
  if (data && patientData !== undefined && snapshot === null && !run.isFetching && !patientSnap.isFetching) setSnapshot(takeSnapshot(data, patientData));

  // Re-check: read the run and Patient again and take a fresh snapshot of both. A rejection is never retried by itself.
  // If either read fails, the rejection stays on screen and an error shows; the rejection is never cleared silently.
  const [recheckFailed, setRecheckFailed] = useState(false);
  const recheck = async () => {
    setRecheckFailed(false);
    const [freshRun, freshPatient] = await Promise.all([run.refetch(), patientSnap.refetch()]);
    if (freshRun.data && !freshRun.isError && freshPatient.data !== undefined && !freshPatient.isError) {
      setSnapshot(takeSnapshot(freshRun.data, freshPatient.data));
      approve.reset();
    } else {
      setRecheckFailed(true);
    }
  };

  if (run.isError && !data) return <p role="alert" className={SECONDARY}>The care-model run could not be read.</p>;
  if (patientSnap.isError && patientData === undefined) {
    return <p role="alert" className={SECONDARY}>The Patient plan and consent revisions could not be read.</p>;
  }
  if (!data || !snapshot) return <p className={SECONDARY}>Reading the care-model run.</p>;

  const waiting = isWaitingOnA3(data);
  const log = a3LogEntries(data);
  const holds = a3Holds(data.holds);
  const result = approve.data ?? null;
  const noConsent = hasNoConsent(snapshot.patient);
  const notAllowed = approve.error instanceof ApiError && approve.error.status === 403;
  const changes =
    result?.kind === 'stale'
      ? changedRevisions(result.snapshot, result.body.revisions ?? data.revisions, result.patientSnapshot, result.body.patientRevisions)
      : [];
  const heldBy = result?.kind === 'held' ? blockingHolds(result.body, data.holds) : [];
  const recheckButton = (
    <span>
      <Button variant="secondary" size="sm" onClick={() => void recheck()} disabled={run.isFetching}>
        Re-check
      </Button>
    </span>
  );

  return (
    <>
      <p className={BODY}>
        <span className="mr-2">{`Run status: ${data.runStatus}`}</span>
        <StatusChip semantic={waiting ? 'info' : 'neutral'} tone="soft">
          {waiting ? 'Waiting on A3' : 'Not waiting on A3'}
        </StatusChip>
      </p>
      <p className={SECONDARY}>{`Snapshot taken ${formatSnapshotTime(snapshot.takenAt)}`}</p>
      <RevisionList revisions={data.revisions} patient={snapshot.patient} />
      {noConsent && (
        <div data-gate="no-consent" className="flex items-center gap-2 px-3 py-2">
          <StatusChip semantic="error" tone="soft">
            Blocked
          </StatusChip>
          <span className={EMPHASIS}>{NO_CONSENT_MESSAGE}</span>
        </div>
      )}

      <span className={SUBTITLE}>A3 log</span>
      <ul aria-label="A3 log" className="flex flex-col">
        {log.length === 0 && <li className={cn('px-3 py-1', SECONDARY)}>No A3 entries yet</li>}
        {log.map((l) => (
          <li key={l.effectId} data-log={l.effectId} className="flex flex-col gap-1 border-b border-border-subtle px-3 py-2">
            <span className={BODY}>{`Outcomes: ${(l.outcomes ?? []).join(', ') || 'none'} · Status: ${l.status ?? 'not recorded'}`}</span>
            <span className={SECONDARY}>{`${l.at ? formatSnapshotTime(l.at) : 'time not recorded'}${l.note ? ` · ${l.note}` : ''}`}</span>
          </li>
        ))}
      </ul>

      <span className={SUBTITLE}>{HOLD_LABEL}</span>
      <ul aria-label={HOLD_LABEL} className="flex flex-col">
        {holds.length === 0 && <li className={cn('px-3 py-1', SECONDARY)}>No holds on A3</li>}
        {holds.map((h) => (
          <HoldRow key={h.holdId} hold={h} enrolmentId={enrolmentId} />
        ))}
      </ul>

      {waiting && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" size="sm" onClick={() => approve.mutate({ revisions: snapshot.revisions, patient: snapshot.patient })} disabled={approve.isPending || noConsent}>
            Approve activation
          </Button>
          {noConsent && <span className={SECONDARY}>{NO_CONSENT_REASON}</span>}
          {/* Consent recorded after the panel loaded: Re-check takes a fresh snapshot, so Approve does not wait for a reload. */}
          {noConsent && !result && recheckButton}
          {env.l360DemoHoldEnabled && (
            <>
              <Button variant="secondary" size="sm" onClick={() => demoHold.mutate()} disabled={demoHold.isPending}>
                Raise demo hold
              </Button>
              <Simulated />
              <span className={SECONDARY}>{DEMO_HOLD_NOTE}</span>
            </>
          )}
        </div>
      )}
      {approve.isError && !notAllowed && <p role="alert" className={SECONDARY}>Activation could not be sent. Nothing was changed.</p>}
      {notAllowed && <p role="alert" data-outcome="not-allowed" className={EMPHASIS}>{NOT_ALLOWED_MESSAGE}</p>}
      {recheckFailed && <p role="alert" className={SECONDARY}>{RECHECK_FAILED_MESSAGE}</p>}
      {demoHold.isError && <p role="alert" className={SECONDARY}>The demo hold could not be raised.</p>}

      {result?.kind === 'routed' && (
        <p role="status" data-outcome="approved" className={EMPHASIS}>
          {APPROVED_MESSAGE}
          {result.patientRevisions ? ` ${checkedAgainst(result.patientRevisions)}` : ''}
        </p>
      )}
      {result?.kind === 'stale' && (
        <div role="status" data-outcome="stale" className="flex flex-col gap-1 px-3 py-2">
          <span className={EMPHASIS}>{STALE_MESSAGE}</span>
          <ul aria-label="Changed revisions" className="flex flex-col">
            {changes.map((c) => (
              <li key={c.key} className={SECONDARY}>{`${c.label}: was ${c.was}, now ${c.now}`}</li>
            ))}
          </ul>
          {recheckButton}
        </div>
      )}
      {result?.kind === 'consent-required' && (
        <div role="status" data-outcome="consent-required" className="flex flex-col gap-1 px-3 py-2">
          <span className={EMPHASIS}>{result.body.patientRevisions ? CONSENT_REQUIRED_MESSAGE : CONSENT_UNKNOWN_MESSAGE}</span>
          {recheckButton}
        </div>
      )}
      {result?.kind === 'held' && (
        <div role="status" data-outcome="held" className="flex flex-col gap-1 px-3 py-2">
          <span className={EMPHASIS}>{HELD_MESSAGE}</span>
          <ul aria-label="Blocking holds" className="flex flex-col">
            {heldBy.map((h) => (
              <li key={h.holdId} className={SECONDARY}>{`Hold: ${h.reason ?? 'No reason recorded'}`}</li>
            ))}
          </ul>
          {recheckButton}
        </div>
      )}
    </>
  );
}

/** A3 for one plan: finds the plan's enrolment by definitionId, then shows that run. */
export function A3Section({ patientId, carePlanId, definitionId, resnapshot = 0 }: { patientId: string; carePlanId: string; definitionId: string | null; resnapshot?: number }) {
  const enrolment = useA3Enrolment(patientId, definitionId);
  const enrolmentId = enrolment.data?.enrolmentId ?? null;
  return (
    <div data-node="A3" aria-label="A3 ready to activate" role="group" className="flex flex-col gap-2">
      <h3 className={EMPHASIS}>Ready to activate?</h3>
      {enrolment.isError && <p role="alert" className={SECONDARY}>The care-model runs could not be read.</p>}
      {enrolment.isSuccess && enrolmentId === null && <p className={SECONDARY}>{NO_RUN_MESSAGE}</p>}
      {enrolmentId !== null && <RunBody key={enrolmentId} enrolmentId={enrolmentId} patientId={patientId} carePlanId={carePlanId} resnapshot={resnapshot} />}
    </div>
  );
}
